import { defineStore } from 'pinia';
import { ref } from 'vue';

import { IResult, IResultCreate, IResultCreateResponse } from '@pssa/types';

import { xhr, xhrServerless } from '@/helpers/service.ts';

import { useClassStore } from './useClassStore';
import { useCompetitorStore } from './useCompetitorStore';

interface IResultForm {
  _id: string;
  classId: string;
  personId: string;
  points: string;
  vbull: string;
}

export const initialCurrentResult = {
  _id: '',
  classId: '',
  personId: '',
  points: '',
  vbull: '',
};

export const useResultsStore = defineStore('result', () => {
  const competitorStore = useCompetitorStore();
  const classStore = useClassStore();
  const results = ref<IResult[]>([]);
  const isLoading = ref(false);
  const currentResult = ref<IResultForm>({ ...initialCurrentResult });
  const isEditing = ref(false);

  async function getResults(id: string) {
    isLoading.value = true;
    results.value = await xhrServerless(`/api/results?id=${id}`);
    isLoading.value = false;
  }

  async function deleteResult(id: string) {
    results.value = results.value.filter(({ _id }) => _id !== id);
    currentResult.value = { ...initialCurrentResult, classId: currentResult.value.classId };
    isEditing.value = false;
    await xhr(`/results/${id}`, { method: 'delete' });
  }

  function findResult(id: string) {
    return results.value.find((result) => result._id === id);
  }

  function editResult(id: string) {
    const result = findResult(id);

    if (result) {
      isEditing.value = true;
      const { _id, classId, personId, points, vbull } = result;
      currentResult.value = {
        _id,
        classId,
        personId,
        points: String(points),
        vbull: String(vbull),
      };
    }
  }

  async function createResult(body: IResultCreate) {
    const response: IResultCreateResponse = await xhr('/results', {
      method: 'post',
      body,
    });
    let competitor = '';
    let classLabel = '';

    const { classId, personId, points, rank, score, vbull, _id } = response;
    const foundCompetitor = competitorStore.findCompetitor(personId);
    const foundClass = classStore.findClass(classId);

    if (foundCompetitor) {
      ({ label: competitor } = foundCompetitor);
    }

    if (foundClass) {
      ({ label: classLabel } = foundClass);
    }

    const result = {
      _id,
      personId,
      points,
      vbull,
      score,
      rank,
      competitor,
      class: classLabel,
      classId,
      club: 'PSSA', // TODO: should get this from actual member data
      // TODO: or find out why classId cannot be used to correctly show members on results being added
    };

    results.value = [...results.value, result];

    return result;
  }

  async function updateResult(id: string, body: IResultCreate) {
    const response: IResultCreateResponse = await xhr(`/results/${id}`, {
      method: 'put',
      body,
    });
    let competitor = '';
    let classLabel = '';

    const { personId, classId } = response;
    const foundCompetitor = competitorStore.findCompetitor(personId);
    const foundClass = classStore.findClass(classId);
    const resultIndex = results.value.findIndex(({ _id }) => _id === id);

    if (foundCompetitor) {
      ({ label: competitor } = foundCompetitor);
    }

    if (foundClass) {
      ({ label: classLabel } = foundClass);
    }

    currentResult.value = { ...initialCurrentResult, classId: currentResult.value.classId };
    results.value[resultIndex] = {
      ...results.value[resultIndex],
      ...response,
      competitor,
      class: classLabel,
      classId,
    };
  }

  function resetResults() {
    results.value = [];
  }

  async function getOverallScores(year: number, classId: string) {
    const results = await xhr(`/results/overall/${year}/${classId}`);

    return results;
  }

  return {
    createResult,
    currentResult,
    deleteResult,
    editResult,
    findResult,
    getOverallScores,
    getResults,
    isEditing,
    isLoading,
    resetResults,
    results,
    updateResult,
  };
});
